import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import Swal from 'sweetalert2';

import { AtelierService, Atelier } from 'app/services/atelier/atelier.service';
import { ServicesService } from 'app/services/services/services.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';

@Component({
  selector: 'app-ajouter-atelier',
  templateUrl: './ajouter-atelier.component.html',
  styleUrls: ['./ajouter-atelier.component.scss']
})
export class AjouterAtelierComponent implements OnInit {
  // Atelier data (matches the Atelier interface)
  atelier: Atelier = {
    name: '',
    prix: '',               // initialisé vide, mais non obligatoire
    description: '',
    mainImageUrl: '',
    galleryImages: [],
    openingTime: '',
    closingTime: '',
    activeDays: [],
    sessionDurations: [],
    serviceId: 0
  };

  service: any;
  idService: string | null = null;
  agencyId: string | null = null;

  // Main image
  mainImage: string | null = null;
  mainImageFile: File | null = null;
  isUploadingMain: boolean = false;

  // Gallery
  galleryImages: string[] = [];
  selectedGalleryFiles: FileList | null = null;
  isUploadingGallery: boolean = false;

  // Form state
  isSubmitting: boolean = false;

  // Custom duration
  customDurationInput: number | null = null;

  // Days of week
  daysOfWeek = [
    { value: 1, label: 'Lundi' },
    { value: 2, label: 'Mardi' },
    { value: 3, label: 'Mercredi' },
    { value: 4, label: 'Jeudi' },
    { value: 5, label: 'Vendredi' },
    { value: 6, label: 'Samedi' },
    { value: 7, label: 'Dimanche' }
  ];

  // Predefined durations
  predefinedDurations = [
    { value: 30, label: '30 min' },
    { value: 45, label: '45 min' },
    { value: 60, label: '1 heure' },
    { value: 90, label: '1h30' },
    { value: 120, label: '2 heures' },
    { value: 180, label: '3 heures' },
    { value: 240, label: '4 heures' }
  ];

  // Checkbox states (managed with buttons)
  dayChecks: boolean[] = [false, false, false, false, false, false, false];
  durationChecks: boolean[] = [];

  @ViewChild('mainImageInput') mainImageInput!: ElementRef<HTMLInputElement>;
  @ViewChild('galleryFileInput') galleryFileInput!: ElementRef<HTMLInputElement>;

  constructor(
    private atelierService: AtelierService,
    private serviceService: ServicesService,
    private awsService: AwscontrolService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.durationChecks = this.predefinedDurations.map(() => false);
  }

  ngOnInit(): void {
    this.idService = this.activatedRoute.snapshot.paramMap.get('id');
    this.loadServiceDetails();
    this.serviceService.refreshNedeed.subscribe(() => this.loadServiceDetails());
  }

  loadServiceDetails(): void {
    if (this.idService) {
      this.serviceService.getService(this.idService).subscribe({
        next: (data: any) => {
          this.service = data;
          this.atelier.serviceId = data.id;
          if (data.user?.id) {
            this.agencyId = data.user.id.toString();
          } else if (data.userId) {
            this.agencyId = data.userId.toString();
          } else if (data.agencyId) {
            this.agencyId = data.agencyId.toString();
          }
        },
        error: (error) => {
          console.error('Erreur chargement service:', error);
          Swal.fire('Erreur', 'Impossible de charger les détails du service', 'error');
        }
      });
    }
  }

  // ----- Text formatting -----
 formatText(command: string): void {
  if (!this.atelier.description) this.atelier.description = '';
  const textarea = document.getElementById('descriptionTextarea') as HTMLTextAreaElement;
  if (!textarea) return;
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const selectedText = this.atelier.description.substring(start, end);
  let formattedText = '';
  switch (command) {
    case 'bold': formattedText = `<strong>${selectedText}</strong>`; break;
    case 'italic': formattedText = `<em>${selectedText}</em>`; break;
    case 'underline': formattedText = `<u>${selectedText}</u>`; break;
    case 'paragraph': formattedText = `<p>${selectedText}</p>`; break;
    case 'list':
      const lines = selectedText.split('\n');
      formattedText = '<ul>\n' + lines.map(line => `  <li>${line}</li>`).join('\n') + '\n</ul>';
      break;
    case 'h1': formattedText = `<h1>${selectedText}</h1>`; break;
    case 'h2': formattedText = `<h2>${selectedText}</h2>`; break;
    case 'linebreak':   // ✅ Nouveau cas
      formattedText = '<br>';
      break;
  }
  const newText = this.atelier.description.substring(0, start) + formattedText + this.atelier.description.substring(end);
  this.atelier.description = newText;
  setTimeout(() => {
    textarea.focus();
    textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
  }, 0);
}

  // ----- Day toggle (chip buttons) -----
  toggleDay(index: number): void {
    if (!this.atelier.activeDays) this.atelier.activeDays = [];
    this.dayChecks[index] = !this.dayChecks[index];
    const dayValue = this.daysOfWeek[index].value;
    if (this.dayChecks[index]) {
      if (!this.atelier.activeDays.includes(dayValue)) {
        this.atelier.activeDays.push(dayValue);
      }
    } else {
      const pos = this.atelier.activeDays.indexOf(dayValue);
      if (pos > -1) {
        this.atelier.activeDays.splice(pos, 1);
      }
    }
  }

  // ----- Duration toggle (chip buttons) -----
  toggleDuration(index: number): void {
    if (!this.atelier.sessionDurations) this.atelier.sessionDurations = [];
    this.durationChecks[index] = !this.durationChecks[index];
    const durationValue = this.predefinedDurations[index].value;
    if (this.durationChecks[index]) {
      if (!this.atelier.sessionDurations.includes(durationValue)) {
        this.atelier.sessionDurations.push(durationValue);
      }
    } else {
      const pos = this.atelier.sessionDurations.indexOf(durationValue);
      if (pos > -1) {
        this.atelier.sessionDurations.splice(pos, 1);
      }
    }
  }

  // ----- Custom duration -----
  addCustomDuration(): void {
    if (!this.atelier.sessionDurations) this.atelier.sessionDurations = [];
    if (!this.customDurationInput || this.customDurationInput <= 0) {
      Swal.fire('Valeur invalide', 'Veuillez saisir un nombre positif (en minutes)', 'warning');
      return;
    }
    if (this.atelier.sessionDurations.includes(this.customDurationInput)) {
      Swal.fire('Déjà présent', 'Cette durée est déjà sélectionnée.', 'info');
      return;
    }
    this.atelier.sessionDurations.push(this.customDurationInput);
    this.customDurationInput = null;
  }

  removeDuration(duration: number): void {
    if (!this.atelier.sessionDurations) return;
    const index = this.atelier.sessionDurations.indexOf(duration);
    if (index > -1) {
      this.atelier.sessionDurations.splice(index, 1);
    }
    const predIndex = this.predefinedDurations.findIndex(d => d.value === duration);
    if (predIndex > -1) {
      this.durationChecks[predIndex] = false;
    }
  }

  getDurationLabel(duration: number): string {
    if (duration < 60) return `${duration} min`;
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    if (minutes === 0) return `${hours} heure${hours > 1 ? 's' : ''}`;
    return `${hours}h${minutes}`;
  }

  // ----- Main Image Upload -----
  triggerMainImageUpload(): void {
    if (!this.isUploadingMain) {
      this.mainImageInput.nativeElement.click();
    }
  }

  onMainImageSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;
    if (!this.validateImageFile(file)) return;
    this.mainImageFile = file;
    this.uploadMainImage();
  }

  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      Swal.fire('Format non supporté', 'JPEG, PNG, JPG, GIF, WEBP uniquement', 'warning');
      return false;
    }
    if (file.size > maxSize) {
      Swal.fire('Fichier trop volumineux', 'Max 5MB', 'warning');
      return false;
    }
    return true;
  }

  uploadMainImage(): void {
    if (!this.mainImageFile) return;
    this.isUploadingMain = true;
    this.awsService.uploadFile(this.mainImageFile, 'atelier-main-images').subscribe({
      next: (response) => {
        this.isUploadingMain = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.mainImage = response.urls[0];
          this.atelier.mainImageUrl = this.mainImage;
          Swal.fire({ icon: 'success', title: 'Image téléchargée', timer: 1500, showConfirmButton: false });
          if (this.mainImageInput) this.mainImageInput.nativeElement.value = '';
          this.mainImageFile = null;
        } else {
          throw new Error(response.error || 'Upload failed');
        }
      },
      error: (err) => {
        this.isUploadingMain = false;
        console.error(err);
        Swal.fire('Erreur', 'Impossible de télécharger l\'image principale', 'error');
      }
    });
  }

  removeMainImage(): void {
    if (!this.mainImage) return;
    Swal.fire({
      title: 'Supprimer l\'image principale ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await this.awsService.deleteFile(this.mainImage!).toPromise();
          this.mainImage = null;
          this.atelier.mainImageUrl = '';
          Swal.fire('Supprimée', '', 'success');
        } catch (error) {
          Swal.fire('Erreur', 'Impossible de supprimer l\'image', 'error');
        }
      }
    });
  }

  // ----- Gallery Upload -----
  triggerGalleryUpload(): void {
    if (!this.isUploadingGallery) {
      this.galleryFileInput.nativeElement.click();
    }
  }

  onGalleryFilesSelected(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.selectedGalleryFiles = files;
      this.uploadGalleryImages();
      event.target.value = '';
    }
  }

  uploadGalleryImages(): void {
    if (!this.selectedGalleryFiles || this.selectedGalleryFiles.length === 0) return;
    this.isUploadingGallery = true;

    Swal.fire({
      title: 'Téléchargement en cours...',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading()
    });

    const files = Array.from(this.selectedGalleryFiles);
    const validFiles: File[] = files.filter(f => this.validateImageFile(f));

    if (validFiles.length === 0) {
      Swal.close();
      Swal.fire('Aucune image valide', '', 'warning');
      this.isUploadingGallery = false;
      return;
    }

    this.awsService.uploadFiles(validFiles, 'atelier-gallery-images').subscribe({
      next: (response) => {
        this.isUploadingGallery = false;
        Swal.close();
        if (response.success && response.urls && response.urls.length > 0) {
          this.galleryImages.push(...response.urls);
          this.atelier.galleryImages = this.galleryImages;
          Swal.fire({
            icon: 'success',
            title: 'Images téléchargées',
            text: `${response.urls.length} image(s) ajoutée(s)`,
            timer: 1500,
            showConfirmButton: false
          });
          if (this.galleryFileInput) this.galleryFileInput.nativeElement.value = '';
        } else {
          throw new Error(response.error || 'Upload failed');
        }
      },
      error: (err) => {
        this.isUploadingGallery = false;
        Swal.close();
        console.error(err);
        Swal.fire('Erreur', 'Impossible de télécharger les images', 'error');
      }
    });
  }

  deleteGalleryImage(index: number): void {
    const imageUrl = this.galleryImages[index];
    Swal.fire({
      title: 'Supprimer cette image ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await this.awsService.deleteFile(imageUrl).toPromise();
          this.galleryImages.splice(index, 1);
          this.atelier.galleryImages = this.galleryImages;
          Swal.fire('Supprimée', '', 'success');
        } catch (error) {
          Swal.fire('Erreur', 'Impossible de supprimer l\'image', 'error');
        }
      }
    });
  }

  // ----- Validation (prix is no longer required) -----
  validateForm(): boolean {
    if (this.isUploadingMain || this.isUploadingGallery) {
      Swal.fire('Téléchargement en cours', 'Veuillez attendre', 'warning');
      return false;
    }
    if (!this.atelier.name?.trim()) {
      Swal.fire('Nom requis', '', 'warning');
      return false;
    }
    // ✅ Le prix n'est plus obligatoire – on supprime le test
    // if (!this.atelier.prix?.trim()) { ... }

    if (!this.atelier.description?.trim()) {
      Swal.fire('Description requise', '', 'warning');
      return false;
    }
    if (!this.mainImage) {
      Swal.fire('Image principale requise', '', 'warning');
      return false;
    }
    if (!this.atelier.openingTime || !this.atelier.closingTime) {
      Swal.fire('Horaires requis', '', 'warning');
      return false;
    }
    if (!this.atelier.activeDays || this.atelier.activeDays.length === 0) {
      Swal.fire('Sélectionnez au moins un jour', '', 'warning');
      return false;
    }
    if (!this.atelier.sessionDurations || this.atelier.sessionDurations.length === 0) {
      Swal.fire('Sélectionnez au moins une durée', '', 'warning');
      return false;
    }
    return true;
  }

  // ----- Submit (CORRECTED) -----
  onSubmit(): void {
    if (!this.validateForm()) return;
    if (!this.idService) {
      Swal.fire('Erreur', 'ID service manquant', 'error');
      return;
    }

    this.isSubmitting = true;
    Swal.fire({
      title: 'Création en cours...',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading()
    });

    // Prepare data – do NOT include serviceId in the body
    const atelierData = {
      name: this.atelier.name.trim(),
      prix: this.atelier.prix?.trim() || '',   // Si vide, on envoie une chaîne vide (ou null selon backend)
      description: this.atelier.description,
      mainImageUrl: this.mainImage,
      galleryImages: this.galleryImages,
      openingTime: this.atelier.openingTime,
      closingTime: this.atelier.closingTime,
      activeDays: this.atelier.activeDays,
      sessionDurations: this.atelier.sessionDurations
    };

    // Appel au service avec l'ID du service en paramètre
    this.atelierService.createAtelier(atelierData, Number(this.idService)).subscribe({
      next: () => {
        Swal.close();
        this.isSubmitting = false;
        Swal.fire({
          icon: 'success',
          title: 'Succès !',
          text: 'Atelier créé',
          timer: 2000,
          showConfirmButton: false
        }).then(() => {
          this.router.navigate(['/list-Ateliers', this.idService]);
        });
      },
      error: (error: any) => {
        Swal.close();
        this.isSubmitting = false;
        let msg = 'Erreur lors de la création';
        if (error.status === 400) msg = 'Données invalides.';
        else if (error.status === 500) msg = 'Erreur serveur.';
        Swal.fire('Erreur', msg, 'error');
      }
    });
  }

  // ----- Cancel & Reset -----
  cancel(): void {
    if (this.agencyId) {
      this.router.navigate(['/list-Services', this.agencyId]);
    } else {
      this.router.navigate(['listeServices']);
    }
  }

  resetForm(): void {
    Swal.fire({
      title: 'Réinitialiser ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui'
    }).then((result) => {
      if (result.isConfirmed) {
        this.atelier = {
          name: '',
          prix: '',
          description: '',
          mainImageUrl: '',
          galleryImages: [],
          openingTime: '',
          closingTime: '',
          activeDays: [],
          sessionDurations: [],
          serviceId: this.atelier.serviceId || 0
        };
        this.mainImage = null;
        this.galleryImages = [];
        this.mainImageFile = null;
        this.selectedGalleryFiles = null;
        this.customDurationInput = null;
        this.dayChecks = this.dayChecks.map(() => false);
        this.durationChecks = this.durationChecks.map(() => false);
        if (this.mainImageInput) this.mainImageInput.nativeElement.value = '';
        if (this.galleryFileInput) this.galleryFileInput.nativeElement.value = '';
        Swal.fire('Réinitialisé', '', 'success');
      }
    });
  }
}